import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'ws-scripts/modules/common';
import { useDrawer } from '../context/DrawerContext';
import { CLOSE_METHOD } from '../utils/trackEvent';

/**
 * PanelHeader — shared header row used by DrawerPanelBody and SubMenuPanel.
 *
 * Renders an optional left action (back button or title heading) and always
 * renders the close button on the right side.
 *
 * @param {PanelHeaderProps}  props
 * @param {React.ReactNode} [props.leftAction] - Node rendered on the left (e.g. back button)
 * @param {string}       [props.title]         - Heading text shown when no leftAction provided
 * @param {ElementRef}   [props.closeRef]      - Ref forwarded to the close button element
 */
const PanelHeader = ({ leftAction, title, closeRef }) => {
	const labels = useLabels();
	const closeLabel = labels.get('NAV_DRAWER_CLOSE_LABEL');
	const { closeDrawer } = useDrawer();

	// Wrapped so the click's MouseEvent isn't forwarded as closeDrawer's
	// close-method argument.
	const handleClose = useCallback(
		() => closeDrawer(CLOSE_METHOD.CLOSE_BUTTON),
		[closeDrawer]
	);

	return (
		<div
			className="ws-nav-drawer__panel-header pt-3 pb-2 d-flex align-items-center justify-content-between"
			data-testid="panel-header"
		>
			{leftAction || <h2 className="ddc-font-size-small m-0">{title}</h2>}
			<button
				ref={closeRef}
				className="btn btn-link p-0 text-decoration-none ws-nav-drawer__close"
				type="button"
				onClick={handleClose}
				data-testid="panel-header-close-button"
			>
				<i
					className="ddc-icon ddc-icon-remove2 ddc-font-size-large d-flex align-items-center justify-content-center"
					aria-hidden="true"
					data-testid="panel-header-close-icon"
				/>
				<span className="sr-only">{closeLabel}</span>
			</button>
		</div>
	);
};

PanelHeader.propTypes = {
	leftAction: PropTypes.node,
	title: PropTypes.string,
	closeRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	])
};

PanelHeader.defaultProps = {
	leftAction: null,
	title: '',
	closeRef: null
};

export default PanelHeader;
